import { Children, useCallback, useEffect, useId, useMemo, useRef, useState, type HTMLAttributes, type ReactNode } from 'react';
import { Link, useLocation } from 'react-router-dom';
import type {
  AssistantActionItemsResponse,
  AssistantAnalysisResponse,
  AssistantAnalysisRow,
  AssistantBriefDto,
  AssistantRefreshMeetingsResponse,
  AssistantRefreshResponse,
  AssistantResponse,
  DoneOfferActionRequest,
  SetFilingRequest,
  SlackTodayCitationDto,
  SlackTodayMatterDto,
  SlackTodayResponse,
  SlackTodaySweepDto,
  TodoDto,
  UpdateTodoRequest,
} from '../../shared/api-types.js';
import type {
  AssistantBriefCommitment,
  AssistantBriefInconsistency,
  AssistantBriefPayload,
  AssistantBriefPerson,
  AssistantBriefPriorReview,
  AssistantBriefRef,
  AssistantBriefThread,
  AssistantBriefWatchOut,
} from '../../shared/assistant.js';
import { ASSISTANT_THREAD_STATUS_LABELS, SLACK_FINDING_IMPORTANCE_LABELS, type SlackFindingKind, type TodoStatus } from '../../shared/enums.js';
import { api, type ApiFailure } from '../api.js';
import { PATHS } from '../paths.js';
import { useMe } from '../shell/me-context.js';
import { RegionErrorBoundary } from '../shell/RegionErrorBoundary.js';
import {
  Avatar,
  Banner,
  browserTimeZone,
  Button,
  Checkbox,
  Chip,
  CollapsibleSection,
  ConfirmDialog,
  DataTable,
  EM_DASH,
  FailureView,
  Icon,
  IconButton,
  LinkButton,
  Page,
  PageHead,
  ProjectChip,
  RelativeTime,
  Spinner,
  StateView,
  StatusPill,
  TabPanel,
  Tabs,
  failureDetail,
  formatCount,
  formatDate,
  formatDateTime,
  inlineFailureText,
  pluralise,
  useNow,
  useRequest,
  useToast,
  useUrlTab,
  type DataTableColumn,
  type IconName,
  type RequestState,
  type SortState,
} from '../ui/index.js';
import { ProjectChipList, ProjectFilingDialog, projectNames } from './project-filing.js';
import {
  actionGroupCount,
  actionGroupCountLabel,
  actionGroupTexts,
  actionItemGroups,
  actionItemMeta,
  actionItemSlackHref,
  actionItemTarget,
  actionItemsEmptyText,
  assistantTabs,
  analysisActionItemCount,
  analysisFindingCount,
  briefComparedWithText,
  briefCoverageText,
  briefPlainText,
  refreshStartedText,
  briefPriorSubjectKind,
  briefPriorSubjectText,
  briefPriorVerdictPresentation,
  briefEntryTarget,
  briefNames,
  briefReviewCounts,
  briefReviewDisclosureText,
  briefSlackHref,
  canOpenSlackToday,
  firstSlackHref,
  groupBriefPriorReview,
  inconsistencySideLabels,
  meetingTarget,
  sinceLastBriefText,
  refreshMeetingsText,
  refreshNowOffer,
  refreshProgressText,
  shortRefText,
  slackCheckPresentation,
  slackTodayCitationsOf,
  slackTodayCount,
  slackTodayDomId,
  slackTodayTarget,
  slackTodayGaps,
  slackTodayIsQuiet,
  slackTodayNextText,
  slackTodayNoneText,
  slackTodayOfferPresentation,
  slackTodayOffText,
  SLACK_TODAY_DONE_LEAD,
  SLACK_TODAY_RUNNING_TEXT,
  SLACK_TODAY_SKIPPED_TEXT,
  slackTodayReadText,
  slackTodayUpdateKind,
  slackTodayUpdatePresentation,
  threadStatusPresentation,
  THREAD_READING_QUALIFIER,
  UPDATE_READING_QUALIFIER,
  todoTarget,
  type AssistantTabId,
  type BriefNames,
  type EntryTarget,
} from './lib/assistant.js';
import { slackTodayCaveatText, slackTodayClimateText } from './lib/climate.js';
import { slackCitationAuthor, slackCitationDescription, slackPermalinkHref } from './lib/slack-context.js';
import { summaryPresentation } from './lib/summary.js';
import { DONE_OFFER_DISMISSED, DONE_OFFER_LIST_PATH, doneOfferPath, doneOffersCountLine, filedUnderText, statusChangeAnnouncement, withTodoStatus } from './lib/todos.js';
import { DueDate, TodoDoneOfferLine, TodoNotes, TodoSourceLabel, TodoWhy, useToday, type TodoDoneOfferActions } from './todo-table.js';
import './pages.css';

// The Assistant page (owner rulings A1–A9 and A11a of 2026-09-27; design §4): tabs of equal standing.
// General Intelligence is the person's brief — how things are going across the last month
// of meetings, written by Calis from the summaries, to-dos, notes and Slack findings it already holds,
// with "Refresh now" and the state of a brief on its way. Slack today (owner request of 2026-09-30, the
// daily Slack sweep; offered wherever Slack is switched on) is what Calis's latest daily look at the
// person's Slack found — the matters that concern them and what the day showed of the brief's open
// items, each with the messages behind it — and what that look did not read. Action items are every open to-do Calis made
// from Slack or across meetings, grouped by meeting with Done, Dismiss and "Why?", beside the meeting's
// "From Slack" notes. Analysis is the window's meetings with what their summaries and Slack checks
// found, sortable by every column, each row leading to the meeting's Slack tab. The head's "Refresh
// recent meetings" summarises and checks every meeting of the window again, after a confirmation that
// says how many and what leaves Calis, and shows the progress in the button's place while it runs.
// Everything here is the person's own (the routes' owner filter); nothing on the page is shared.

export const POLL_INTERVAL_MS = 5_000;
/**
 * Thirty minutes of polling (twenty until 2026-10-04): a brief may wait five minutes for new material to
 * settle and then take the provider up to twenty (ASSISTANT_TIMEOUT_MS), so the page keeps watching past
 * both; a refresh of the recent meetings runs its summaries and checks one after another, behind live
 * work, and can outlast this, after which "Check again" is offered. Exported so a test ties it to the
 * server's timings: it drifted behind them once.
 */
export const POLL_MAX = 360;

const ANALYSIS_DEFAULT_SORT: SortState = { key: 'date', dir: 'desc' };

type AnalysisColumnKey = 'date' | 'title' | 'summary' | 'slack' | 'context' | 'inconsistencies' | 'opportunities' | 'action_items';

function referenceOf(failure: ApiFailure) {
  return failure.correlationId ? <span className="text-xs muted"> Reference <code className="selectable">{failure.correlationId}</code></span> : null;
}

export default function AssistantPage() {
  const me = useMe();
  const toast = useToast();
  const now = useNow(30_000);
  const slackShown = me.integrations.slack !== 'off';
  // Slack today only where the daily sweep runs (measuring or analysing): elsewhere it could only say it is off.
  const sweepShown = me.integrations.slackSweep !== 'off';
  const [tab, setTab] = useUrlTab<AssistantTabId>(assistantTabs(sweepShown), 'intelligence');

  const assistant = useRequest<AssistantResponse>((signal) => api.get<AssistantResponse>('/api/assistant', { signal }), []);
  // Why only where the sweep runs: elsewhere the tab is not offered, and the route would only say so.
  const slackToday = useRequest<SlackTodayResponse | null>(
    (signal) => (sweepShown ? api.get<SlackTodayResponse>('/api/assistant/slack-today', { signal }) : Promise.resolve({ ok: true as const, status: 200, data: null })),
    [sweepShown],
  );
  const actionItems = useRequest<AssistantActionItemsResponse>((signal) => api.get<AssistantActionItemsResponse>('/api/assistant/action-items', { signal }), []);
  const analysis = useRequest<AssistantAnalysisResponse>((signal) => api.get<AssistantAnalysisResponse>('/api/assistant/analysis', { signal }), []);
  const data = assistant.data;

  // Filing a matter by hand (ADR 0017, design §7.4): the Edit control on a Slack today card, offered only under the
  // projects floor. The route answers the whole tab, which replaces what is shown; the toast names the projects and
  // offers Undo, which sends the previous set back the same way (03-components §4.2 prefers undo to confirm).
  const projectsOn = me.integrations.projects === 'on';
  const matterFiling = useMemo<MatterFiling | undefined>(() => (projectsOn
    ? {
      onFiled: (response, change) => {
        slackToday.setData(response);
        const filed = response.sweep?.matters.find((matter) => matter.id === change.matter.id);
        toast.success(filedUnderText(projectNames(filed?.projects ?? [])), {
          key: `matter-filing-${change.matter.id}`,
          action: {
            label: 'Undo',
            onClick: () => {
              void api.post<SlackTodayResponse>(matterProjectsPath(change.matter.id), { projectIds: change.previous } satisfies SetFilingRequest).then((result) => {
                if (!result.ok) {
                  toast.error(<>We couldn&rsquo;t undo that filing. {failureDetail(result)}</>);
                  return;
                }
                slackToday.setData(result.data);
              });
            },
          },
        });
      },
    }
    : undefined), [projectsOn, slackToday, toast]);

  // --- Following a brief or a refresh while it runs (as the meeting page follows a summary) -----------
  // Why the two flags: between an accepted request and the next reading the page would offer the button
  // again for a moment; each holds the pending state until that reading lands, whatever it says.
  const [awaitingBrief, setAwaitingBrief] = useState(false);
  const [awaitingRefresh, setAwaitingRefresh] = useState(false);
  useEffect(() => {
    setAwaitingBrief(false);
    setAwaitingRefresh(false);
  }, [data]);
  const inFlight = data !== null && (data.job !== null || data.refresh !== null);
  const watching = inFlight || awaitingBrief || awaitingRefresh;
  const [polls, setPolls] = useState(0);
  useEffect(() => {
    if (!watching || polls >= POLL_MAX) return;
    const handle = window.setTimeout(() => {
      setPolls((count) => count + 1);
      assistant.reload();
    }, POLL_INTERVAL_MS);
    return () => window.clearTimeout(handle);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [watching, polls]);
  // A brief that landed replaces the Across-meetings items; a refresh that moved on changes what the
  // Analysis tab says of each meeting — both are read again when the reading shows it.
  const briefId = data?.brief?.id ?? null;
  const previousBriefId = useRef<string | null>(null);
  useEffect(() => {
    if (previousBriefId.current !== null && previousBriefId.current !== briefId) actionItems.reload();
    previousBriefId.current = briefId;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [briefId]);
  const refreshMark = data?.refresh ? `${data.refresh.refreshId}:${data.refresh.summaries.done}:${data.refresh.checks.done}:${data.refresh.summaries.failed}:${data.refresh.checks.failed}` : null;
  const previousRefreshMark = useRef<string | null>(null);
  useEffect(() => {
    if (previousRefreshMark.current !== null && previousRefreshMark.current !== refreshMark) {
      analysis.reload();
      actionItems.reload();
    }
    previousRefreshMark.current = refreshMark;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshMark]);

  // --- Refresh now (owner ruling A7) ---------------------------------------------------------------------
  const [requestingBrief, setRequestingBrief] = useState(false);
  const requestBrief = async (): Promise<void> => {
    setRequestingBrief(true);
    const result = await api.post<AssistantRefreshResponse>('/api/assistant/refresh');
    setRequestingBrief(false);
    if (!result.ok) {
      toast.error(<>We couldn&rsquo;t request a brief. {failureDetail(result)}</>);
      // A 409 or 429 means the state moved on (a cooldown, the day's limit): show the current reading.
      if (result.code === 'conflict' || result.code === 'rate_limited') assistant.reload();
      return;
    }
    // Why "it goes first" either way (ADR 0012, 2026-09-28): that the request goes ahead of other work is the one
    // outcome the toast confirms; the pending line beneath says what, if anything, still stands ahead.
    toast.success(result.data.alreadyQueued ? 'A brief is already on its way — it now goes first.' : 'Brief requested — it goes first.');
    setAwaitingBrief(true);
    setPolls(0);
    assistant.reload();
  };

  // --- Refresh recent meetings (owner ruling A11a) -----------------------------------------------------
  const [refreshDialogOpen, setRefreshDialogOpen] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [refreshError, setRefreshError] = useState<ReactNode>(null);
  const refreshMeetings = async (): Promise<void> => {
    setRefreshing(true);
    setRefreshError(null);
    const result = await api.post<AssistantRefreshMeetingsResponse>('/api/assistant/refresh-meetings');
    setRefreshing(false);
    if (!result.ok) {
      setRefreshError(<>{inlineFailureText(result)}{referenceOf(result)}</>);
      // Already under way, or nothing left to refresh: the next reading says which.
      if (result.code === 'conflict') assistant.reload();
      return;
    }
    setRefreshDialogOpen(false);
    const { meetings, joined } = result.data;
    toast.success(refreshStartedText(meetings, joined));
    setAwaitingRefresh(true);
    setPolls(0);
    assistant.reload();
  };

  const brief = data?.state === 'ready' ? data.brief : null;
  const names = useMemo(() => briefNames(analysis.data?.items ?? [], brief, slackToday.data?.sweep ?? null), [analysis.data, brief, slackToday.data]);
  const pollingExhausted = watching && polls >= POLL_MAX;
  const checkAgain = (): void => {
    setPolls(0);
    assistant.reload();
  };

  // --- The head's action: the button, the progress, or the reason ----------------------------------------
  const headAction = (() => {
    if (!data) return null;
    if (data.refresh || awaitingRefresh) {
      const text = data.refresh ? refreshProgressText(data.refresh) : 'Refreshing your recent meetings';
      // Why the spinner carries the words: it is the status region (03-components §12), so the progress is
      // announced once; the visible text beside it says the same and is decorative to a screen reader.
      return (
        <span className="assistant-head-status text-sm secondary">
          <Spinner size={16} label={text} />
          <span aria-hidden="true">{text}…</span>
        </span>
      );
    }
    if (data.eligibleMeetings === 0) {
      return <p className="assistant-head-status text-sm muted">No meetings from the last {formatCount(data.windowDays)} days have a transcript to refresh.</p>;
    }
    return (
      <Button variant="secondary" icon="refresh" onClick={() => { setRefreshError(null); setRefreshDialogOpen(true); }}>
        Refresh recent meetings
      </Button>
    );
  })();

  const tabs = [
    { id: 'intelligence' as const, label: 'General Intelligence' },
    ...(sweepShown ? [{ id: 'slack-today' as const, label: 'Slack today', count: slackTodayCount(slackToday.data) }] : []),
    { id: 'action-items' as const, label: 'Action items', count: actionItems.data?.counts.open },
    { id: 'analysis' as const, label: 'Analysis', count: analysis.data?.items.length },
  ];
  const windowDays = data?.windowDays ?? analysis.data?.windowDays ?? 30;
  // Why one sentence: the tabs beneath name themselves; the window is the one fact the head alone carries (02-patterns §2.1).
  const subtitle = `What Calis makes of your meetings of the last ${formatCount(windowDays)} days.`;

  if (assistant.status === 'error' && assistant.error && !data) {
    return (
      <Page measure="wide">
        <PageHead title="Assistant" subtitle={subtitle} />
        <FailureView failure={assistant.error} what="your assistant" featureName="the assistant" onRetry={assistant.reload} minHeight="40dvh" />
      </Page>
    );
  }

  return (
    <Page measure="wide" className="assistant-page">
      <PageHead title="Assistant" subtitle={subtitle} actions={headAction} />

      {pollingExhausted ? (
        <Banner tone="info" icon="clock" actions={<Button variant="secondary" size="sm" icon="refresh" onClick={checkAgain}>Check again</Button>}>
          This is taking longer than usual. Calis keeps working in the background.
        </Banner>
      ) : null}

      <Tabs<AssistantTabId> id="assistant" label="Assistant sections" tabs={tabs} value={tab} onChange={setTab} />

      <TabPanel tabsId="assistant" tabId="intelligence" active={tab === 'intelligence'} heading="General Intelligence">
        <RegionErrorBoundary region="General Intelligence" compact>
          <IntelligenceTab
            request={assistant}
            names={names}
            now={now}
            awaitingBrief={awaitingBrief}
            requesting={requestingBrief}
            onRefreshNow={() => void requestBrief()}
          />
        </RegionErrorBoundary>
      </TabPanel>

      {sweepShown ? (
        <TabPanel tabsId="assistant" tabId="slack-today" active={tab === 'slack-today'} heading="Slack today">
          <RegionErrorBoundary region="Slack today" compact>
            <SlackTodayTab request={slackToday} now={now} filing={matterFiling} />
          </RegionErrorBoundary>
        </TabPanel>
      ) : null}

      <TabPanel tabsId="assistant" tabId="action-items" active={tab === 'action-items'} heading="Action items">
        <RegionErrorBoundary region="the action items" compact>
          <ActionItemsTab request={actionItems} sweep={slackToday.data?.state === 'ready' ? slackToday.data.sweep : null} onChanged={() => { analysis.reload(); }} />
        </RegionErrorBoundary>
      </TabPanel>

      <TabPanel tabsId="assistant" tabId="analysis" active={tab === 'analysis'} heading="Analysis">
        <RegionErrorBoundary region="the analysis" compact>
          <AnalysisTab request={analysis} brief={brief} names={names} slackShown={slackShown} />
        </RegionErrorBoundary>
      </TabPanel>

      {data ? (
        <RefreshMeetingsDialog
          open={refreshDialogOpen}
          meetings={data.eligibleMeetings}
          windowDays={data.windowDays}
          slackIncluded={data.slackIncluded}
          busy={refreshing}
          error={refreshError}
          onCancel={() => setRefreshDialogOpen(false)}
          onConfirm={() => void refreshMeetings()}
        />
      ) : null}
    </Page>
  );
}

// --- The confirmation (owner ruling A11a) ----------------------------------------------------------------

function RefreshMeetingsDialog({ open, meetings, windowDays, slackIncluded, busy, error, onCancel, onConfirm }: {
  open: boolean;
  meetings: number;
  windowDays: number;
  slackIncluded: boolean;
  busy: boolean;
  error: ReactNode;
  onCancel: () => void;
  onConfirm: () => void;
}) {
  // Whether project names go with the summaries a refresh asks for: only where the projects floor is on (ADR 0017).
  const projectsOn = useMe().integrations.projects === 'on';
  const text = refreshMeetingsText({ meetings, windowDays, slackIncluded, projectsOn });
  return (
    <ConfirmDialog open={open} title={text.title} confirmLabel={text.confirm} busy={busy} error={error} onCancel={onCancel} onConfirm={onConfirm}>
      <div className="stack stack--2">
        {text.body.map((sentence) => <p key={sentence}>{sentence}</p>)}
      </div>
    </ConfirmDialog>
  );
}

// --- General Intelligence -------------------------------------------------------------------------------

interface IntelligenceTabProps {
  request: RequestState<AssistantResponse>;
  names: BriefNames;
  now: Date;
  awaitingBrief: boolean;
  requesting: boolean;
  onRefreshNow: () => void;
}

function IntelligenceTab({ request, names, now, awaitingBrief, requesting, onRefreshNow }: IntelligenceTabProps) {
  const data = request.data;
  if (!data) return <StateView kind="loading" label="Loading your brief" minHeight="16rem" />;

  // Why not "due any moment now" between the request and the next reading: the page does not yet know
  // what stands ahead of the brief; the reading says (lib/assistant.ts refreshNowOffer).
  const offer = awaitingBrief && !data.job ? { kind: 'queued' as const, text: 'Pending · asking for a new brief', runAfter: '' } : refreshNowOffer({ job: data.job, cooldownUntil: data.cooldownUntil, now, refreshInFlight: data.refresh !== null });
  const refreshControl = (() => {
    switch (offer.kind) {
      case 'running':
        return (
          <span className="assistant-refresh__status text-sm secondary">
            <Spinner size={16} label={offer.text} />
            <span aria-hidden="true">{offer.text}…</span>
          </span>
        );
      case 'queued':
        // Why the button stays: a brief queued for later can be brought forward (the request expedites it),
        // so the pending state is said beside the offer rather than in its place.
        return (
          <>
            <span className="assistant-refresh__status text-sm secondary">
              <Spinner size={16} label={offer.text} />
              <span aria-hidden="true">{offer.text}…</span>
            </span>
            <Button variant="secondary" size="sm" icon="refresh" onClick={onRefreshNow} loading={requesting}>Refresh now</Button>
          </>
        );
      case 'cooldown':
        // Why no disabled button: the reason is the answer, shown where the control would be (03-components §2).
        return <p className="text-sm muted">{offer.text}</p>;
      case 'ready':
      default:
        return <Button variant="secondary" size="sm" icon="refresh" onClick={onRefreshNow} loading={requesting}>Refresh now</Button>;
    }
  })();

  const failure = data.lastFailure ? (
    <Banner tone="warning" title="The last brief could not be written">
      <p>{data.lastFailure.message} <span className="muted">(<RelativeTime value={data.lastFailure.at} />)</span></p>
    </Banner>
  ) : null;

  const stateView = (() => {
    switch (data.state) {
      case 'none':
        return (
          <StateView
            kind="empty"
            icon="sparkles"
            title={data.job ? 'Your brief is on its way' : 'No brief yet'}
            description={data.job
              ? 'Calis is reading your recent meetings; the brief appears here when it is ready.'
              : `Calis writes a brief from your meetings of the last ${formatCount(data.windowDays)} days once new summaries or Slack checks arrive.`}
            action={refreshControl}
            minHeight="16rem"
          />
        );
      case 'unreadable':
        return (
          <StateView
            kind="error"
            title="This brief can no longer be opened"
            description="This installation’s Slack key changed, so Calis cannot open the brief it wrote for you. The next brief replaces it."
            action={refreshControl}
            minHeight="16rem"
          />
        );
      case 'withheld':
        return (
          <StateView
            kind="empty"
            icon="lock"
            title="Your latest brief is set aside"
            description="It drew on Slack findings you may no longer see, so it is not shown. The next brief is written without them."
            action={refreshControl}
            minHeight="16rem"
          />
        );
      case 'ready':
      default:
        return null;
    }
  })();

  if (data.state !== 'ready' || data.brief === null) {
    return (
      <div className="stack">
        {failure}
        {stateView}
      </div>
    );
  }

  const brief = data.brief;
  return (
    <div className="stack assistant-brief">
      <div className="assistant-provenance">
        <p className="summary-provenance">
          {brief.isDemo ? <Chip tone="warning" icon="bolt" title="Written by the demo assistant, not by an AI model">Demo</Chip> : null}
          <span>
            Updated <RelativeTime value={brief.updatedAt} /> · {briefCoverageText(brief)}
            {brief.payload.comparedWith ? <> · {briefComparedWithText(brief.payload.comparedWith)}</> : null}
          </span>
        </p>
        <div className="assistant-refresh">{refreshControl}</div>
      </div>
      {failure}
      <BriefBody payload={brief.payload} names={names} />
    </div>
  );
}

/** The brief's sections, in reading order, each of which the reader may fold away. Exported for the rendering tests. */
export function BriefBody({ payload, names }: { payload: AssistantBriefPayload; names: BriefNames }) {
  const today = useToday();
  return (
    <>
      <div className="assistant-headline-block stack stack--2">
        <p className="assistant-headline">{briefPlainText(payload.headline)}</p>
        <p className="assistant-caveat text-sm secondary">
          <Icon name="sparkles" size={14} />
          <span>Written by Calis from your meetings. Check the linked meetings before acting. Only you can see this.</span>
        </p>
      </div>

      <SinceLastBrief payload={payload} names={names} />

      <BriefSection id="assistant-threads" title="Ongoing matters" count={payload.threads.length} emptyText="The brief found no matter running across your meetings.">
        <ol className="assistant-list" aria-label="Ongoing matters">
          {payload.threads.map((thread, index) => <ThreadEntry key={`${thread.title}-${index}`} thread={thread} names={names} />)}
        </ol>
      </BriefSection>

      <div className="assistant-columns">
        <BriefSection id="assistant-you-owe" title="You owe" count={payload.commitments.youOwe.length} emptyText="Nothing open that the meetings put on you.">
          <CommitmentList commitments={payload.commitments.youOwe} names={names} today={today} label="What you owe" showWho={false} />
        </BriefSection>
        <BriefSection id="assistant-owed-to-you" title="Owed to you" count={payload.commitments.owedToYou.length} emptyText="Nothing open that others owe you.">
          <CommitmentList commitments={payload.commitments.owedToYou} names={names} today={today} label="What is owed to you" showWho />
        </BriefSection>
      </div>

      <BriefSection id="assistant-inconsistencies" title="Things that do not agree" count={payload.inconsistencies.length} emptyText="Nothing the brief found disagrees between your meetings, or between a meeting and Slack.">
        <BriefInconsistencies items={payload.inconsistencies} names={names} />
      </BriefSection>

      <div className="assistant-columns">
        <BriefSection id="assistant-watch-outs" title="Watch out for" count={payload.watchOuts.length} emptyText="Nothing is slipping.">
          <ul className="assistant-list" aria-label="Watch out for">
            {payload.watchOuts.map((item, index) => <WatchOutEntry key={`${item.text}-${index}`} item={item} names={names} />)}
          </ul>
        </BriefSection>
        <BriefSection id="assistant-people" title="People to follow up with" count={payload.people.length} emptyText="Nobody in particular.">
          <ul className="assistant-list" aria-label="People to follow up with">
            {payload.people.map((person, index) => <PersonEntry key={`${person.name}-${index}`} person={person} names={names} />)}
          </ul>
        </BriefSection>
      </div>
    </>
  );
}

/**
 * What this brief made of the one before it (owner ruling A13): one line of counts, and — when anything
 * was settled — a disclosure listing each settled entry with the verdict, its words (the way to where it
 * rests) and the reason. What still holds is counted, not listed: it is this brief's body. Nothing when
 * the brief judged no earlier one. Exported for the rendering tests.
 */
export function SinceLastBrief({ payload, names }: { payload: Pick<AssistantBriefPayload, 'priorReview' | 'comparedWith'>; names: BriefNames }) {
  const counts = briefReviewCounts(payload);
  if (counts === null || payload.priorReview === null) return null;
  const groups = groupBriefPriorReview(payload.priorReview);
  const listed = [...groups.changed, ...groups.unclear];
  const headingId = 'assistant-since-last-brief-heading';
  return (
    <section className="assistant-review stack stack--2" aria-labelledby={headingId}>
      <p className="assistant-review__line">
        <span id={headingId} className="assistant-review__title">Since your last brief</span>
        <span className="assistant-review__counts num">{sinceLastBriefText(counts)}</span>
      </p>
      {listed.length > 0 ? (
        <details className="disclosure assistant-review__details">
          <summary>
            <Icon name="chevron-down" size={16} />
            {briefReviewDisclosureText(groups)}
          </summary>
          <div className="disclosure__body">
            <ol className="assistant-review__list" aria-label="Entries of your last brief that changed">
              {listed.map((entry, index) => (
                <BriefReviewLine key={`${entry.ref}-${index}`} entry={entry} names={names} />
              ))}
            </ol>
          </div>
        </details>
      ) : null}
    </section>
  );
}

function BriefReviewLine({ entry, names }: { entry: AssistantBriefPriorReview; names: BriefNames }) {
  const id = useId();
  const { tone, icon, label } = briefPriorVerdictPresentation(entry.verdict);
  // Why the To-dos page whatever the to-do's state: the brief that settled an action item set it aside only while it
  // stood open and untouched (owner ruling A13), so an unclear one, or one the person worked on, is not on the
  // dismissed list — the whole list finds it either way. Plain words once it is deleted (absent from `names.todos`).
  const target: EntryTarget | null = entry.prior.type === 'action_item'
    ? (names.todos.has(entry.prior.todoId) ? todoTarget(entry.prior.todoId, null) : null)
    : briefEntryTarget(entry.refs, names, 'meeting');
  const subject = briefPriorSubjectText(entry.prior);
  return (
    <li className="assistant-review__item stack stack--1" data-verdict={entry.verdict}>
      <div className="assistant-review__head cluster">
        <StatusPill tone={tone} icon={icon}>{label}</StatusPill>
        <span className="text-xs muted">{briefPriorSubjectKind(entry.prior)}</span>
      </div>
      <p className="assistant-review__subject"><EntryLink target={target} describedBy={`${id}-where`}>{subject}</EntryLink></p>
      <p className="text-sm secondary">{entry.reason}</p>
      <EntryLine whereId={`${id}-where`} target={target} slackHref={briefSlackHref(entry.refs, names)} about={subject} />
    </li>
  );
}

/** One of the brief's sections: a heading the reader may fold it under, with its count; the empty text when it has nothing. */
function BriefSection({ id, title, count, emptyText, children }: { id: string; title: string; count: number; emptyText: string; children: ReactNode }) {
  return (
    <CollapsibleSection id={id} title={title} count={count} className="assistant-section">
      {count > 0 ? children : <p className="text-sm muted">{emptyText}</p>}
    </CollapsibleSection>
  );
}

const ENTRY_TARGET_ICONS: Readonly<Record<EntryTarget['kind'], IconName>> = { todo: 'check-square', meeting: 'calendar', slack_today: 'sparkles' };

/**
 * An entry's words as the one way to where it belongs — the to-do, or the meeting's summary (owner request of
 * 2026-10-01); plain words where it rests on nothing the page can open. The link is described by the line beneath
 * it, which says where it leads.
 */
function EntryLink({ target, describedBy, children }: { target: EntryTarget | null; describedBy: string; children: ReactNode }) {
  if (target === null) return <>{children}</>;
  return <Link to={target.href} className="assistant-entry__link hit-area" aria-describedby={describedBy}>{children}</Link>;
}

/**
 * The short line under an entry: what it carries (a person, a due date), where its words lead, and the Slack message
 * it rests on. Nothing when it has none of these.
 */
function EntryLine({ whereId, target, slackHref, about, children }: { whereId: string; target: EntryTarget | null; slackHref: string | null; about: string; children?: ReactNode }) {
  // Why Children.toArray: a commitment passes its who and due as slots that may both be empty ([false, null]).
  if (target === null && slackHref === null && Children.toArray(children).length === 0) return null;
  return (
    <p className="assistant-entry__line text-sm">
      {children}
      {target ? (
        <span id={whereId} className="assistant-entry__where">
          <Icon name={ENTRY_TARGET_ICONS[target.kind]} size={14} />
          <span>{target.label}</span>
        </span>
      ) : null}
      {slackHref ? <SlackMessageLink href={slackHref} described={entrySlackDescription(about)} className="assistant-entry__slack" /> : null}
    </p>
  );
}

/** What an entry's "Open in Slack" opens, for a screen reader: "the message behind “…”". */
function entrySlackDescription(about: string): string {
  return `the message behind “${shortRefText(about)}”`;
}

/**
 * "Open in Slack", straight to a message — the one an entry rests on (owner request of 2026-10-01), or a cited one in
 * its list. D9 (ADR 0009): Slack opens in a new tab, and the link says so to everyone, not only to the sighted. The
 * href has passed `slackPermalinkHref`; `described` says which message it is.
 */
function SlackMessageLink({ href, described, className }: { href: string; described: string; className?: string }) {
  return (
    <LinkButton href={href} target="_blank" rel="noopener noreferrer" variant="link" size="sm" trailingIcon="external" className={['hit-area', className ?? ''].filter(Boolean).join(' ')}>
      Open in Slack<span className="visually-hidden">: {described} (opens Slack in a new tab)</span>
    </LinkButton>
  );
}

function ThreadEntry({ thread, names }: { thread: AssistantBriefThread; names: BriefNames }) {
  const id = useId();
  const target = briefEntryTarget(thread.refs, names, 'meeting');
  const status = threadStatusPresentation(thread.status);
  return (
    <li className="assistant-item stack stack--1">
      <div className="assistant-item__head">
        <p className="assistant-item__title"><EntryLink target={target} describedBy={`${id}-where`}>{thread.title}</EntryLink></p>
        <span className="cluster">
          <span className="text-xs muted">{THREAD_READING_QUALIFIER}</span>
          <StatusPill tone={status.tone} icon={status.icon}>{ASSISTANT_THREAD_STATUS_LABELS[thread.status]}</StatusPill>
        </span>
      </div>
      <p className="assistant-entry__text">{briefPlainText(thread.summary)}</p>
      {thread.nextStep ? <p className="assistant-entry__text"><span className="secondary">Next: </span>{thread.nextStep}</p> : null}
      <EntryLine whereId={`${id}-where`} target={target} slackHref={briefSlackHref(thread.refs, names)} about={thread.title} />
    </li>
  );
}

function CommitmentList({ commitments, names, today, label, showWho }: { commitments: readonly AssistantBriefCommitment[]; names: BriefNames; today: string; label: string; showWho: boolean }) {
  return (
    <ul className="assistant-list" aria-label={label}>
      {commitments.map((item, index) => <CommitmentEntry key={`${item.text}-${index}`} item={item} names={names} today={today} showWho={showWho} />)}
    </ul>
  );
}

function CommitmentEntry({ item, names, today, showWho }: { item: AssistantBriefCommitment; names: BriefNames; today: string; showWho: boolean }) {
  const id = useId();
  // Why the to-do first: a commitment is something to do, and its to-do is where it is ticked off.
  const target = briefEntryTarget(item.refs, names, 'todo');
  return (
    <li className="assistant-item stack stack--1">
      <p className="assistant-entry__statement"><EntryLink target={target} describedBy={`${id}-where`}>{item.text}</EntryLink></p>
      <EntryLine whereId={`${id}-where`} target={target} slackHref={briefSlackHref(item.refs, names)} about={item.text}>
        {showWho && item.who ? <span className="assistant-entry__who">{item.who}</span> : null}
        {item.due ? <DueDate dueDate={item.due} status="open" today={today} /> : item.overdue ? <StatusPill tone="danger" icon="alert-triangle">Overdue</StatusPill> : null}
      </EntryLine>
    </li>
  );
}

function WatchOutEntry({ item, names }: { item: AssistantBriefWatchOut; names: BriefNames }) {
  const id = useId();
  const target = briefEntryTarget(item.refs, names, 'meeting');
  return (
    <li className="assistant-item stack stack--1">
      <p className="assistant-entry__statement"><EntryLink target={target} describedBy={`${id}-where`}>{item.text}</EntryLink></p>
      <EntryLine whereId={`${id}-where`} target={target} slackHref={briefSlackHref(item.refs, names)} about={item.text} />
    </li>
  );
}

function PersonEntry({ person, names }: { person: AssistantBriefPerson; names: BriefNames }) {
  const id = useId();
  const target = briefEntryTarget(person.refs, names, 'meeting');
  return (
    <li className="assistant-item stack stack--1">
      <p className="assistant-entry__name"><EntryLink target={target} describedBy={`${id}-where`}>{person.name}</EntryLink></p>
      <p className="assistant-entry__text">{person.why}</p>
      <EntryLine whereId={`${id}-where`} target={target} slackHref={briefSlackHref(person.refs, names)} about={`${person.name}: ${person.why}`} />
    </li>
  );
}

/** The cross-meeting inconsistencies, side against side (shown on both the brief and the Analysis tab). Exported for the rendering tests. */
export function BriefInconsistencies({ items, names }: { items: readonly AssistantBriefInconsistency[]; names: BriefNames }) {
  return (
    <ol className="assistant-list" aria-label="Things that do not agree">
      {items.map((item, index) => <InconsistencyEntry key={`${item.title}-${index}`} item={item} names={names} />)}
    </ol>
  );
}

function InconsistencyEntry({ item, names }: { item: AssistantBriefInconsistency; names: BriefNames }) {
  const labels = inconsistencySideLabels(item.whichIsLater);
  return (
    <li className="assistant-item assistant-inconsistency stack stack--2">
      <div className="assistant-item__head">
        <p className="assistant-item__title">{item.title}</p>
        <span className="cluster">
          <StatusPill tone={item.importance === 'high' ? 'warning' : 'neutral'} icon={item.importance === 'high' ? 'arrow-up' : item.importance === 'low' ? 'arrow-down' : 'minus'}>
            {SLACK_FINDING_IMPORTANCE_LABELS[item.importance]} importance
          </StatusPill>
          {/* Why only when in doubt: "fairly sure" on every entry was a pill of words that said nothing (owner, 2026-10-01). */}
          {item.confidence !== 'high' ? <StatusPill tone="neutral" icon="help-circle" title="How sure the brief is that these two really disagree">{item.confidence === 'medium' ? 'Probable' : 'Unsure'}</StatusPill> : null}
        </span>
      </div>
      <div className="slack-sides">
        <InconsistencySide label={labels.a} text={item.sideA} refs={item.refsA} names={names} />
        <InconsistencySide label={labels.b} text={item.sideB} refs={item.refsB} names={names} />
      </div>
      {item.whichIsLater === 'unclear' ? <p className="text-sm muted">Calis cannot tell which was said later.</p> : null}
    </li>
  );
}

/** One side of a disagreement: what was said, as the way to where it was said, and the Slack message when it was Slack. */
function InconsistencySide({ label, text, refs, names }: { label: string; text: string; refs: readonly AssistantBriefRef[]; names: BriefNames }) {
  const id = useId();
  const target = briefEntryTarget(refs, names, 'meeting');
  return (
    <div className="slack-side stack stack--1">
      <p className="slack-side__label">{label}</p>
      <p className="assistant-entry__statement"><EntryLink target={target} describedBy={`${id}-where`}>{text}</EntryLink></p>
      <EntryLine whereId={`${id}-where`} target={target} slackHref={briefSlackHref(refs, names)} about={text} />
    </div>
  );
}

// --- Slack today (owner request of 2026-09-30: the daily Slack sweep) ------------------------------------

const SLACK_TODAY_IMPORTANCE_ICONS = { high: 'arrow-up', medium: 'minus', low: 'arrow-down' } as const;

/** The filing route of a matter (routes/assistant.ts): the person's whole set of projects for it. */
export function matterProjectsPath(matterId: string): string {
  return `/api/assistant/matters/${encodeURIComponent(matterId)}/projects`;
}

/**
 * How the page files a matter by hand (ADR 0017): present only under the projects floor, so a card without it carries
 * the matter's chips and no Edit control — the rendering tests render the tab without one.
 */
export interface MatterFiling {
  /** The tab as the route answered it after a filing, and what changed, so the page can replace it and offer Undo. */
  onFiled: (response: SlackTodayResponse, change: { matter: SlackTodayMatterDto; previous: string[]; next: string[] }) => void;
}

/**
 * The latest daily look at the person's Slack (03-components §1, §6.2, §7): the four states — loading without the
 * panel collapsing, an empty state that says what would appear and why it has not, the permission state where Slack
 * findings are withheld, and a failure with a way to try again — then, when there is a look to show, one banner for
 * what it did not read and a card per matter and per update. A list of cards, never a table: each entry is prose
 * with its messages beneath it. Exported for the rendering tests.
 */
export function SlackTodayTab({ request, now, filing }: { request: RequestState<SlackTodayResponse | null>; now: Date; filing?: MatterFiling }) {
  if (request.status === 'loading' && !request.data) return <StateView kind="loading" label="Loading what Calis found in your Slack" minHeight="16rem" />;
  if (request.status === 'error' && request.error && !request.data) return <FailureView failure={request.error} what="what Calis found in your Slack" featureName="the assistant" onRetry={request.reload} minHeight="16rem" />;
  const data = request.data;
  if (!data) return null;
  const connections = <LinkButton to={PATHS.connections} variant="secondary" size="sm">Open Connections</LinkButton>;
  const checkAgain = <Button variant="secondary" size="sm" icon="refresh" onClick={request.reload} loading={request.refreshing}>Check again</Button>;

  switch (data.state) {
    case 'off': {
      const text = slackTodayOffText(data.offReason);
      // Why the skipped look is said here (the owner's ruling Q6(a) of escalation 20261005-200526; 05-content §6): a look
      // the person's own switch stopped owes a record visible to them alone, Skipped with its reason beside the word; the
      // server sends it on this tab and nowhere else.
      const skipped = data.offReason === 'auto_check_off' && data.skippedLook
        ? <p className="text-sm muted">{SLACK_TODAY_SKIPPED_TEXT} · <RelativeTime value={data.skippedLook.at} mode="both" /></p>
        : null;
      const description = skipped ? <><p>{text.description}</p>{skipped}</> : text.description;
      return <StateView kind={data.offReason === 'installation' ? 'not-configured' : 'empty'} icon={data.offReason === 'installation' ? 'unplug' : 'clock'} title={text.title} description={description} action={text.connections ? connections : undefined} minHeight="16rem" />;
    }
    case 'withheld':
      return (
        <StateView
          kind="denied"
          title="What Calis found in your Slack is not shown"
          description="Your access does not include Slack at the moment, so what Calis found in your Slack is set aside unopened. Ask an administrator to add you to a group that grants it."
          minHeight="16rem"
        />
      );
    case 'running':
      return <StateView kind="empty" icon="clock" title="Calis is looking at your Slack now" description={SLACK_TODAY_RUNNING_TEXT} action={checkAgain} minHeight="16rem" />;
    case 'failed':
      return (
        <StateView
          kind="error"
          title="Calis’s last look at your Slack did not complete"
          description={<><p>{data.failure?.message}</p>{data.failure ? <p className="text-sm muted">{slackTodayNextText(data.failure.retryAt, now)} · it stopped <RelativeTime value={data.failure.at} /></p> : null}</>}
          action={checkAgain}
          minHeight="16rem"
        />
      );
    case 'none':
      return <StateView kind="empty" icon="clock" title="No look at your Slack yet" description={slackTodayNoneText(data.next, now)} minHeight="16rem" />;
    case 'ready':
    default:
      return data.sweep ? <SlackTodayBody sweep={data.sweep} now={now} filing={filing} /> : null;
  }
}

/** What the latest look found, under where it came from; each section may be folded away. Exported for the rendering tests. */
export function SlackTodayBody({ sweep, now, filing }: { sweep: SlackTodaySweepDto; now: Date; filing?: MatterFiling }) {
  const gaps = slackTodayGaps(sweep);
  // The matter whose projects are being chosen ("Edit filing"); the dialog sends the set and hands the tab's answer up.
  const [filingMatter, setFilingMatter] = useState<SlackTodayMatterDto | null>(null);
  // Arriving by #st-<id> — a reference in the brief: bring the matter or update into view, highlight and focus it,
  // or say it is no longer here (a later look settled it; 06-modules: a deep-linked target is identified or the page
  // says it could not be). Once found it is left alone, so a reload never moves the page. A folded section holding
  // the target opens for it (CollapsibleSection revealFor), in the same render, before this looks for it.
  const location = useLocation();
  const target = useMemo(() => slackTodayTarget(location.hash), [location.hash]);
  const located = useRef<string | null>(null);
  const [missing, setMissing] = useState<string | null>(null);
  useEffect(() => {
    if (target === null || located.current === target) return;
    const element = document.getElementById(slackTodayDomId(target));
    if (!element) {
      setMissing(target);
      return;
    }
    located.current = target;
    setMissing(null);
    const frame = requestAnimationFrame(() => {
      element.scrollIntoView({ block: 'center', behavior: 'auto' });
      element.focus({ preventScroll: true });
    });
    return () => cancelAnimationFrame(frame);
  }, [target, sweep]);
  const revealIn = (items: readonly { id: string }[]): string | null => (target !== null && items.some((item) => item.id === target) ? target : null);
  const card = (id: string): Pick<HTMLAttributes<HTMLLIElement>, 'id' | 'tabIndex' | 'aria-labelledby' | 'aria-current' | 'className'> => ({
    id: slackTodayDomId(id),
    tabIndex: -1,
    'aria-labelledby': `${slackTodayDomId(id)}-title`,
    'aria-current': target === id ? 'true' : undefined,
    className: ['slack-finding', 'record-anchor', target === id ? 'is-highlighted' : ''].filter(Boolean).join(' '),
  });
  return (
    <div className="stack slack-today">
      {missing !== null ? (
        <Banner tone="info">
          This is no longer open in your Slack: a later look found it settled, or it was replaced.
        </Banner>
      ) : null}
      <div className="assistant-provenance">
        <p className="summary-provenance">
          <span>
            Updated <RelativeTime value={sweep.completedAt} /> · {slackTodayReadText(sweep)}
          </span>
        </p>
        <p className="text-sm muted">{slackTodayNextText(sweep.nextDueAt, now)}</p>
      </div>
      {gaps.length > 0 ? (
        <Banner tone="warning" title="This look at your Slack is incomplete">
          {gaps.map((gap) => <p key={gap}>{gap}</p>)}
        </Banner>
      ) : null}
      {/* The day's climate (ADR 0019; design §2.5, §2.14 items 4 and 7): the person's own, said with the author line's
          clause exactly when the server sent one — it sends one only while executive intelligence is on and the look
          answered it. A reading with its qualifier, never a pill (05-content §6.1). */}
      {sweep.climate ? <p className="text-sm secondary" data-climate="slack-today">{slackTodayClimateText(sweep.climate)}</p> : null}
      <p className="assistant-caveat text-sm secondary">
        <Icon name="sparkles" size={14} />
        <span>{slackTodayCaveatText(sweep.climate !== null)}</span>
      </p>
      {slackTodayIsQuiet(sweep) ? (
        <StateView
          kind="empty"
          icon="check"
          title="A quiet day"
          description="Nothing Calis read in your Slack since its last look concerns you or the open matters in your brief."
          minHeight="12rem"
        />
      ) : (
        <>
          <CollapsibleSection id="slack-today-matters" title="Matters in Slack" count={sweep.matters.length} className="assistant-section" revealFor={revealIn(sweep.matters)}>
            {sweep.matters.length > 0 ? (
              <ol className="slack-findings" aria-label="Matters in Slack">
                {sweep.matters.map((matter) => {
                  // Why the first meeting only: one place per entry (owner, 2026-10-01); the matter's other meetings are a click away.
                  const meeting = matter.meetings[0] ?? null;
                  const destination = meeting ? meetingTarget(meeting.id, meeting.title) : null;
                  const domId = slackTodayDomId(matter.id);
                  return (
                    <li key={matter.id} {...card(matter.id)} data-slack-matter={matter.id}>
                      <div className="slack-finding__pills cluster">
                        {matter.isNew ? <StatusPill tone="info" icon="sparkles">New today</StatusPill> : <StatusPill tone="neutral" icon="clock" title={formatDateTime(matter.firstSeenAt)}>{`Open since ${formatDate(matter.firstSeenAt)}`}</StatusPill>}
                        <StatusPill tone="neutral" icon={SLACK_TODAY_IMPORTANCE_ICONS[matter.importance]}>{`${SLACK_FINDING_IMPORTANCE_LABELS[matter.importance]} importance`}</StatusPill>
                        {matter.confidence === 'low' ? <StatusPill tone="neutral" icon="help-circle" title="Calis was not sure of this one">Possible</StatusPill> : null}
                      </div>
                      <h4 id={`${domId}-title`} className="slack-finding__title"><EntryLink target={destination} describedBy={`${domId}-where`}>{matter.title}</EntryLink></h4>
                      <p className="slack-finding__detail">{matter.detail}</p>
                      <EntryLine whereId={`${domId}-where`} target={destination} slackHref={firstSlackHref(matter.citationIds, sweep.citations)} about={matter.title} />
                      {/* The matter's projects (ADR 0017, owner ruling 3): the chips, and — under the floor — the one control that changes them. */}
                      {filing || matter.projects.length > 0 ? (
                        <p className="slack-finding__projects text-sm cluster">
                          <span className="secondary">Projects: </span>
                          <ProjectChipList projects={matter.projects} />
                          {filing ? <IconButton icon="edit" label={`Change projects for “${matter.title}”`} size="sm" onClick={() => setFilingMatter(matter)} /> : null}
                        </p>
                      ) : null}
                      <SlackTodayMessages citations={slackTodayCitationsOf(matter.citationIds, sweep.citations)} label={`The Slack messages behind “${matter.title}”`} />
                    </li>
                  );
                })}
              </ol>
            ) : (
              <p className="text-sm muted">Nothing new or still open in Slack concerns you.</p>
            )}
          </CollapsibleSection>
          {filing && filingMatter ? (
            <ProjectFilingDialog<SlackTodayResponse>
              open
              title="Change projects"
              description={<>The projects <strong>{filingMatter.title}</strong> is filed under. Your choice stands when Calis looks at your Slack again.</>}
              initial={filingMatter.projects.map((project) => project.id)}
              onClose={() => setFilingMatter(null)}
              onApply={(projectIds) => api.post<SlackTodayResponse>(matterProjectsPath(filingMatter.id), { projectIds } satisfies SetFilingRequest)}
              onApplied={(response, change) => {
                setFilingMatter(null);
                filing.onFiled(response, { matter: filingMatter, previous: change.previous, next: change.next });
              }}
            />
          ) : null}
          <CollapsibleSection id="slack-today-updates" title="Your brief’s open items" count={sweep.updates.length} className="assistant-section" revealFor={revealIn(sweep.updates)}>
            {sweep.updates.length > 0 ? (
              <ol className="slack-findings" aria-label="What Slack showed about your brief">
                {sweep.updates.map((update) => {
                  const pill = slackTodayUpdatePresentation(update.status);
                  const domId = slackTodayDomId(update.id);
                  return (
                    <li key={update.id} {...card(update.id)} data-slack-update={update.id}>
                      <div className="assistant-item__head">
                        <span className="text-xs muted">{slackTodayUpdateKind(update.subjectType)}</span>
                        <span className="cluster">
                          <span className="text-xs muted">{UPDATE_READING_QUALIFIER}</span>
                          <StatusPill tone={pill.tone} icon={pill.icon}>{pill.label}</StatusPill>
                        </span>
                      </div>
                      <h4 id={`${domId}-title`} className="slack-finding__title">{update.subject}</h4>
                      <p className="slack-finding__detail">{update.sentence}</p>
                      <EntryLine whereId={`${domId}-where`} target={null} slackHref={firstSlackHref(update.citationIds, sweep.citations)} about={update.subject} />
                      <SlackTodayMessages citations={slackTodayCitationsOf(update.citationIds, sweep.citations)} label={`The Slack messages behind the update on “${update.subject}”`} />
                    </li>
                  );
                })}
              </ol>
            ) : (
              <p className="text-sm muted">Slack showed nothing new about the open matters in your brief.</p>
            )}
          </CollapsibleSection>
          <CollapsibleSection id="slack-today-todos" title="Your to-dos in Slack" count={sweep.todoNotes.length} className="assistant-section" revealFor={revealIn(sweep.todoNotes)}>
            {sweep.todoNotes.length > 0 ? (
              <ol className="slack-findings" aria-label="What Slack showed about your to-dos">
                {sweep.todoNotes.map((note) => {
                  const domId = slackTodayDomId(note.id);
                  const destination = todoTarget(note.todo.id, null);
                  return (
                    <li key={note.id} {...card(note.id)} data-slack-todo-note={note.id}>
                      <div className="assistant-item__head">
                        <span className="text-xs muted">{note.kind === 'verdict' ? 'Taken off your list' : 'Your to-do'}</span>
                        {note.kind === 'verdict' ? <StatusPill tone="neutral" icon="check">Dismissed by Calis</StatusPill> : null}
                      </div>
                      <h4 id={`${domId}-title`} className="slack-finding__title"><EntryLink target={destination} describedBy={`${domId}-where`}>{note.todo.text}</EntryLink></h4>
                      <p className="slack-finding__detail">{note.text}</p>
                      <EntryLine whereId={`${domId}-where`} target={destination} slackHref={firstSlackHref(note.citationIds, sweep.citations)} about={note.todo.text} />
                      <SlackTodayMessages citations={slackTodayCitationsOf(note.citationIds, sweep.citations)} label={`The Slack messages behind the note on “${note.todo.text}”`} />
                    </li>
                  );
                })}
              </ol>
            ) : (
              <p className="text-sm muted">Slack showed nothing new about your to-dos.</p>
            )}
          </CollapsibleSection>
          {sweep.doneSignals.length > 0 ? (
            <CollapsibleSection id="slack-today-done" title="Suggested as done" count={sweep.doneSignals.length} className="assistant-section" revealFor={revealIn(sweep.doneSignals)}>
              <div className="stack stack--2">
                <p className="text-sm secondary">{SLACK_TODAY_DONE_LEAD}</p>
                <ol className="slack-findings" aria-label="To-dos Slack suggests are done">
                  {sweep.doneSignals.map((signal) => {
                    const pill = slackTodayOfferPresentation(signal.offer);
                    const domId = slackTodayDomId(signal.id);
                    const destination = todoTarget(signal.todo.id, null);
                    return (
                      <li key={signal.id} {...card(signal.id)} data-slack-done-signal={signal.id}>
                        <div className="assistant-item__head">
                          <span className="text-xs muted">Your to-do</span>
                          <StatusPill tone={pill.tone} icon={pill.icon}>{pill.label}</StatusPill>
                        </div>
                        <h4 id={`${domId}-title`} className="slack-finding__title"><EntryLink target={destination} describedBy={`${domId}-where`}>{signal.todo.text}</EntryLink></h4>
                        <p className="slack-finding__detail">{signal.reason}</p>
                        <EntryLine whereId={`${domId}-where`} target={destination} slackHref={firstSlackHref(signal.citationIds, sweep.citations)} about={signal.todo.text} />
                        <SlackTodayMessages citations={slackTodayCitationsOf(signal.citationIds, sweep.citations)} label={`The Slack messages behind the suggestion that “${signal.todo.text}” is done`} />
                      </li>
                    );
                  })}
                </ol>
              </div>
            </CollapsibleSection>
          ) : null}
        </>
      )}
    </div>
  );
}

/**
 * The messages behind an entry, folded (owner request of 2026-10-01: fewer words per entry): the first one is already
 * a click away through "Open in Slack" above; the quotes, authors and conversations are there for whoever wants them.
 */
function SlackTodayMessages({ citations, label }: { citations: readonly SlackTodayCitationDto[]; label: string }) {
  if (citations.length === 0) return null;
  return (
    <details className="disclosure assistant-messages">
      <summary>
        <Icon name="chevron-down" size={16} />
        {pluralise(citations.length, { one: 'Show the message', other: 'Show # messages' })}
      </summary>
      <div className="disclosure__body">
        <SlackTodayCitations citations={citations} label={label} />
      </div>
    </details>
  );
}

function SlackTodayCitations({ citations, label }: { citations: readonly SlackTodayCitationDto[]; label: string }) {
  if (citations.length === 0) return null;
  return (
    <ol className="slack-citations" aria-label={label}>
      {citations.map((citation) => {
        const author = slackCitationAuthor(citation);
        const href = slackPermalinkHref(citation.permalink);
        const described = slackCitationDescription(citation, browserTimeZone());
        return (
          <li key={citation.id} className="slack-citation">
            <Avatar name={author} size="sm" decorative />
            <div className="slack-citation__body stack stack--1">
              <p className="slack-citation__meta">
                <span className="slack-citation__author">{author}</span>
                <span>{citation.conversationLabel}</span>
                <time dateTime={citation.postedAt}>{formatDateTime(citation.postedAt)}</time>
              </p>
              <blockquote className="slack-citation__excerpt">{citation.quote}</blockquote>
              {href ? (
                <div className="slack-citation__actions cluster">
                  <SlackMessageLink href={href} described={described} />
                </div>
              ) : null}
            </div>
          </li>
        );
      })}
    </ol>
  );
}

// --- Action items ---------------------------------------------------------------------------------------

function ActionItemsTab({ request, sweep, onChanged }: { request: RequestState<AssistantActionItemsResponse>; sweep: SlackTodaySweepDto | null; onChanged: () => void }) {
  const me = useMe();
  const toast = useToast();
  const today = useToday();
  // Whether what the daily sweep noted may lead to "Slack today" (the tab is offered only where the sweep runs).
  const slackToday = canOpenSlackToday(useMe());
  const [busy, setBusy] = useState<ReadonlySet<string>>(new Set());

  const setStatus = useCallback(async (todo: TodoDto, status: TodoStatus, options: { announce: string; undoTo: TodoStatus }) => {
    const previous = todo.status;
    const optimisticAt = new Date().toISOString();
    const patch = (next: TodoStatus, at: string) =>
      request.setData((current) => (current ? { ...current, groups: current.groups.map((group) => ({ ...group, items: withTodoStatus(group.items, todo.id, next, at) })) } : current));
    setBusy((current) => new Set(current).add(todo.id));
    patch(status, optimisticAt);
    const result = await api.patch<TodoDto>(`/api/todos/${encodeURIComponent(todo.id)}`, { status } satisfies UpdateTodoRequest);
    setBusy((current) => {
      const next = new Set(current);
      next.delete(todo.id);
      return next;
    });
    if (!result.ok) {
      patch(previous, todo.updatedAt);
      toast.error(<>We couldn&rsquo;t update that to-do. {failureDetail(result)}</>);
      if (result.code === 'not_found') request.reload();
      return;
    }
    toast.success(options.announce, {
      key: `todo-${todo.id}`,
      action: {
        label: 'Undo',
        onClick: () => {
          void api.patch<TodoDto>(`/api/todos/${encodeURIComponent(todo.id)}`, { status: options.undoTo } satisfies UpdateTodoRequest).then((undo) => {
            if (!undo.ok) {
              toast.error(<>We couldn&rsquo;t undo that. {failureDetail(undo)}</>);
              return;
            }
            request.reload();
            onChanged();
          });
        },
      },
    });
    request.reload();
    onChanged();
  }, [onChanged, request, toast]);

  // Mark done and Not done under the daily sweep's suggestion (owner ruling Q8(b)): the person's own tick, made by the
  // server through the checkbox's path — so it is announced and undone as the checkbox's tick is here. A suggestion
  // that no longer stands (409) or is gone (404) reads the tab again and says so; nothing was ticked.
  const decideOffer = useCallback(async (todo: TodoDto, action: 'accept' | 'dismiss') => {
    if (!todo.doneOffer) return;
    setBusy((current) => new Set(current).add(todo.id));
    const result = await api.post<TodoDto>(doneOfferPath(todo.id, action), { offerId: todo.doneOffer.id } satisfies DoneOfferActionRequest);
    setBusy((current) => {
      const next = new Set(current);
      next.delete(todo.id);
      return next;
    });
    if (!result.ok) {
      if (result.code === 'conflict') toast.warning(result.message);
      else toast.error(<>{action === 'accept' ? 'We couldn’t mark that to-do done.' : 'We couldn’t turn that suggestion down.'} {failureDetail(result)}</>);
      request.reload();
      onChanged();
      return;
    }
    if (action === 'accept') {
      const announce = statusChangeAnnouncement({ from: 'open', asked: 'done', landed: result.data.status, landedVia: result.data.completedVia, link: result.data.link, fellowQueued: result.data.fellow?.pending === 'waiting' });
      toast.success(announce, {
        key: `todo-${todo.id}`,
        action: {
          label: 'Undo',
          onClick: () => {
            void api.patch<TodoDto>(`/api/todos/${encodeURIComponent(todo.id)}`, { status: 'open', expectedStatus: result.data.status } satisfies UpdateTodoRequest).then((undo) => {
              if (!undo.ok) {
                toast.error(<>We couldn&rsquo;t undo that. {failureDetail(undo)}</>);
                return;
              }
              request.reload();
              onChanged();
            });
          },
        },
      });
    } else {
      toast.success(DONE_OFFER_DISMISSED, { key: `todo-${todo.id}` });
    }
    request.reload();
    onChanged();
  }, [onChanged, request, toast]);
  const offerActions = useMemo<TodoDoneOfferActions>(() => ({ accept: (todo) => void decideOffer(todo, 'accept'), dismiss: (todo) => void decideOffer(todo, 'dismiss') }), [decideOffer]);
  // The Slack message behind each to-do: a meeting check's as the server resolved it, the daily look's from Slack today.
  const serverSlackLinks = useMemo(() => new Map((request.data?.slackLinks ?? []).map((link) => [link.todoId, link.permalink])), [request.data]);
  const slackHrefOf = (todo: TodoDto): string | null => actionItemSlackHref(todo, serverSlackLinks, sweep);

  const toggleDone = (todo: TodoDto): void => {
    if (todo.status === 'done') void setStatus(todo, 'open', { announce: 'Reopened', undoTo: 'done' });
    else void setStatus(todo, 'done', { announce: 'Marked as completed', undoTo: todo.status });
  };
  const dismiss = (todo: TodoDto): void => {
    // The same words as the meeting's Slack tab says of a dismissal (slack-context-tab.tsx): one toast, two places.
    void setStatus(todo, 'dismissed', { announce: 'Dismissed. It stays on your to-do list under Dismissed; later checks will not add it again.', undoTo: todo.status });
  };

  if (request.status === 'loading' && !request.data) return <StateView kind="loading" label="Loading your action items" minHeight="16rem" />;
  if (request.status === 'error' && request.error && !request.data) return <FailureView failure={request.error} what="your action items" featureName="the assistant" onRetry={request.reload} minHeight="16rem" />;
  const groups = actionItemGroups(request.data ?? { groups: [] });
  // Why done items stay until the next reading: a ticked row leaves the list on reload, and Undo is offered
  // meanwhile; a dismissed one is set aside at once, as everywhere else.
  const shown = groups.map((group) => ({ ...group, items: group.items.filter((todo) => todo.status !== 'dismissed') })).filter((group) => group.items.length > 0 || group.notedTodos.length > 0);
  // The daily sweep's suggestions stand under any of the person's to-dos, not only these: the count leads to them all.
  const offered = request.data?.counts.doneOffers ?? 0;
  const offersLine = offered > 0 ? (
    <p className="assistant-offers text-sm">
      <Icon name="check-square" size={14} className="assistant-offers__icon" />
      <Link to={DONE_OFFER_LIST_PATH} className="hit-area">{doneOffersCountLine(offered)}</Link>
    </p>
  ) : null;

  if (shown.length === 0) {
    return (
      <div className="stack">
        {offersLine}
        <StateView
          kind="empty"
          icon="check-square"
          title="Nothing to decide on"
          description={actionItemsEmptyText(me.integrations.slackSweep === 'on')}
          action={<LinkButton to={PATHS.todos} variant="secondary" size="sm">Open the to-do list</LinkButton>}
          minHeight="16rem"
        />
      </div>
    );
  }

  return (
    <div className="stack">
      {offersLine}
      {shown.map((group) => {
        const { title } = group;
        const texts = actionGroupTexts(group);
        // Grouped by project, a group names no meeting, so each to-do says its own (design §3.4).
        const metaOf = (todo: TodoDto): string | null => (group.byProject ? actionItemMeta(todo) : null);
        return (
          <CollapsibleSection
            key={group.key}
            id={`assistant-group-${group.key}`}
            // A project's group is headed by its own label chip (design §7.7): the name is the chip's text, so the
            // colour is never the only signal; no link inside the heading's button (link={false}).
            title={group.project ? <ProjectChip project={group.project} link={false} /> : title}
            count={actionGroupCount(group)}
            countLabel={() => actionGroupCountLabel(group)}
            className="assistant-group card"
            data-action-group={group.key}
            aside={group.meeting ? (
              <>
                {group.meeting.startedAt ? <span className="text-xs muted num">{formatDate(group.meeting.startedAt)}</span> : null}
                <LinkButton to={PATHS.meeting(group.meeting.id)} variant="link" size="sm">
                  Meeting summary<span className="visually-hidden">: {group.meeting.title}</span>
                </LinkButton>
              </>
            ) : null}
          >
            <div className="stack stack--3">
              {group.items.length > 0 ? (
                <ol className="what-to-do__list" aria-label={`Action items for ${title}`}>
                  {group.items.map((todo) => (
                    <ActionItemRow
                      key={todo.id}
                      todo={todo}
                      busy={busy.has(todo.id)}
                      today={today}
                      slackToday={slackToday}
                      slackHref={slackHrefOf(todo)}
                      offerActions={offerActions}
                      meta={metaOf(todo)}
                      onToggle={() => toggleDone(todo)}
                      onDismiss={() => dismiss(todo)}
                    />
                  ))}
                </ol>
              ) : (
                <p className="text-sm muted">{texts.nothingLeft}</p>
              )}
              {group.notedTodos.length > 0 ? (
                <div className="assistant-noted stack stack--1">
                  <p className="text-sm secondary">{texts.notedLead}</p>
                  <ul className="assistant-noted__list" aria-label={texts.notedLabel}>
                    {group.notedTodos.map((todo) => (
                      <NotedTodo key={todo.id} todo={todo} meta={metaOf(todo)} slackHref={slackHrefOf(todo)} busy={busy.has(todo.id)} slackToday={slackToday} offerActions={offerActions} />
                    ))}
                  </ul>
                </div>
              ) : null}
            </div>
          </CollapsibleSection>
        );
      })}
    </div>
  );
}

/**
 * Where an action item's words lead, said beside them as the brief's entries say it — the cue that needs no hover (a
 * touch screen has none). Just "To-do" when the group's own heading names the meeting; grouped by project it does not,
 * so the line names where the to-do came from as well — "To-do · Weekly sync", "To-do · Your Slack day" (`meta`).
 */
function actionItemWhere(todo: TodoDto, meta: string | null): EntryTarget {
  return { ...actionItemTarget(todo), label: meta === null ? 'To-do' : `To-do · ${meta}` };
}

/** One of the meeting's other to-dos that Slack noted: its words lead to it, then where they lead, Slack, its notes. */
function NotedTodo({ todo, meta, slackHref, busy, slackToday, offerActions }: { todo: TodoDto; meta: string | null; slackHref: string | null; busy: boolean; slackToday: boolean; offerActions: TodoDoneOfferActions }) {
  const id = useId();
  const target = actionItemWhere(todo, meta);
  return (
    <li className="assistant-noted__item stack stack--1">
      <p><EntryLink target={target} describedBy={`${id}-where`}>{todo.text}</EntryLink></p>
      <EntryLine whereId={`${id}-where`} target={target} slackHref={slackHref} about={todo.text} />
      <TodoDoneOfferLine todo={todo} actions={offerActions} busy={busy} slackToday={slackToday} />
      <TodoNotes todo={todo} slackToday={slackToday} />
    </li>
  );
}

/**
 * One action item: the box ticks it off; its words are the way to the to-do itself (owner request of 2026-10-01 —
 * the words used to be the box's label, so a click meant to open it ticked it off instead); beneath them where it came
 * from, when it is due, who holds it and the Slack message it rests on; Dismiss at the end of the row. Exported for
 * the rendering tests.
 */
export function ActionItemRow({ todo, busy, today, slackToday, slackHref, offerActions, meta = null, onToggle, onDismiss }: {
  todo: TodoDto;
  busy: boolean;
  today: string;
  slackToday: boolean;
  slackHref: string | null;
  offerActions: TodoDoneOfferActions;
  /** Where the to-do came from, when its group does not say (grouped by project: `actionItemMeta`); null otherwise. */
  meta?: string | null;
  onToggle: () => void;
  onDismiss: () => void;
}) {
  const id = useId();
  const done = todo.status === 'done';
  return (
    <li className={['what-to-do__item', done ? 'is-done' : ''].filter(Boolean).join(' ')} data-action-item={todo.id}>
      <div className="what-to-do__row">
        <Checkbox
          className="todo-done what-to-do__check"
          checked={done}
          disabled={busy}
          onChange={onToggle}
          label={<span className="visually-hidden">{done ? `Reopen “${todo.text}”` : `Mark “${todo.text}” as completed`}</span>}
        />
        <div className="what-to-do__body stack stack--1">
          <p>
            <Link to={actionItemTarget(todo).href} className={['assistant-entry__link', 'todo-text', 'hit-area', done ? 'is-done' : ''].filter(Boolean).join(' ')} aria-describedby={`${id}-where`}>{todo.text}</Link>
          </p>
          <p className="assistant-entry__line text-sm">
            <TodoSourceLabel todo={todo} />
            {todo.dueDate ? <DueDate dueDate={todo.dueDate} status={todo.status} today={today} /> : null}
            {todo.responsible ? <span>{todo.responsible}</span> : null}
            <span id={`${id}-where`} className="assistant-entry__where">
              <Icon name={ENTRY_TARGET_ICONS.todo} size={14} />
              <span>{actionItemWhere(todo, meta).label}</span>
            </span>
            {slackHref ? <SlackMessageLink href={slackHref} described={entrySlackDescription(todo.text)} className="assistant-entry__slack" /> : null}
          </p>
        </div>
        <div className="what-to-do__actions cluster">
          <Button variant="ghost" size="sm" icon="x" disabled={busy} onClick={onDismiss}>
            Dismiss<span className="visually-hidden">: {todo.text}</span>
          </Button>
        </div>
      </div>
      <TodoDoneOfferLine todo={todo} actions={offerActions} busy={busy} slackToday={slackToday} />
      {todo.notes.length > 0 ? <div className="what-to-do__item-notes"><TodoNotes todo={todo} slackToday={slackToday} /></div> : null}
      <TodoWhy todo={todo} slackToday={slackToday} />
    </li>
  );
}

// --- Analysis -------------------------------------------------------------------------------------------

function AnalysisTab({ request, brief, names, slackShown }: { request: RequestState<AssistantAnalysisResponse>; brief: AssistantBriefDto | null; names: BriefNames; slackShown: boolean }) {
  // Folding (04-data §1.7; ADR 0168): the two states (Summary, Slack check — scanning) first, then the counts — context
  // and opportunities before action items and inconsistencies, what the tab is read for — and Date, the default sort,
  // last. The meeting names the row and never folds.
  const columns = useMemo<DataTableColumn<AssistantAnalysisRow>[]>(() => {
    const countColumn = (key: AnalysisColumnKey, header: string, kind: SlackFindingKind, fold: number): DataTableColumn<AssistantAnalysisRow> => ({
      key,
      header,
      fold,
      type: 'number',
      numeric: true,
      sortValue: (row) => analysisFindingCount(row, kind),
      cell: (row) => {
        const count = analysisFindingCount(row, kind);
        return count === null ? <span className="muted" aria-label="Not checked">{EM_DASH}</span> : formatCount(count);
      },
    });
    const all: DataTableColumn<AssistantAnalysisRow>[] = [
      { key: 'date', header: 'Date', type: 'date', width: '8rem', card: 'meta', fold: 7, sortValue: (row) => row.startedAt, cell: (row) => <span className="num">{formatDate(row.startedAt)}</span> },
      {
        key: 'title',
        header: 'Meeting',
        type: 'text',
        card: 'title',
        sortValue: (row) => row.title,
        // Why the Slack tab: the row is about what the checks found; the tab is where that is read.
        cell: (row) => <Link to={`${PATHS.meeting(row.id)}?tab=slack`} className="hit-area assistant-analysis__title">{row.title}</Link>,
      },
      {
        key: 'summary',
        header: 'Summary',
        type: 'enum',
        card: 'meta',
        fold: 1,
        sortValue: (row) => summaryPresentation(row.summaryStatus, row.transcriptStatus, row.processedAt).rank,
        cell: (row) => {
          const state = summaryPresentation(row.summaryStatus, row.transcriptStatus, row.processedAt);
          return <StatusPill tone={state.tone} icon={state.icon} title={state.detail}>{state.word}</StatusPill>;
        },
      },
      ...(slackShown
        ? [{
            key: 'slack' as const,
            header: 'Slack check',
            type: 'enum' as const,
            card: 'meta' as const,
            fold: 2,
            sortValue: (row: AssistantAnalysisRow) => slackCheckPresentation(row.slackCheck).rank,
            cell: (row: AssistantAnalysisRow) => {
              const state = slackCheckPresentation(row.slackCheck);
              return <StatusPill tone={state.tone} icon={state.icon} title={state.detail}>{state.word}</StatusPill>;
            },
          }]
        : []),
      countColumn('context', 'Context', 'context', 3),
      countColumn('inconsistencies', 'Inconsistencies', 'inconsistency', 6),
      countColumn('opportunities', 'Opportunities', 'opportunity', 4),
      { key: 'action_items', header: 'Action items', type: 'number', numeric: true, fold: 5, sortValue: (row) => analysisActionItemCount(row), cell: (row) => formatCount(analysisActionItemCount(row)) },
    ];
    return all;
  }, [slackShown]);

  const windowDays = request.data?.windowDays ?? 30;
  const status = request.status === 'loading' && !request.data
    ? { kind: 'loading' as const, rowCount: 6 }
    : request.status === 'error' && request.error && !request.data
      ? { kind: 'error' as const, failure: request.error, onRetry: request.reload }
      : { kind: 'ready' as const };

  return (
    <div className="stack">
      <CollapsibleSection id="assistant-analysis-across" title="Across your meetings" count={brief ? brief.payload.inconsistencies.length : undefined} className="assistant-section">
        {brief === null ? (
          <p className="text-sm muted">What does not agree between your meetings appears here once your brief is written.</p>
        ) : brief.payload.inconsistencies.length === 0 ? (
          <p className="text-sm muted">Nothing the brief found disagrees between your meetings, or between a meeting and Slack.</p>
        ) : (
          <BriefInconsistencies items={brief.payload.inconsistencies} names={names} />
        )}
      </CollapsibleSection>
      <CollapsibleSection
        id="assistant-analysis-meetings"
        title="Meeting by meeting"
        count={request.data ? request.data.items.length : undefined}
        countLabel={(count) => pluralise(count, { one: '# meeting', other: '# meetings' })}
        className="assistant-section"
      >
        {/* The caption and the column headers say what the table holds; no lead above it (04-data §1). */}
        <DataTable<AssistantAnalysisRow, AnalysisColumnKey>
          caption={`Meetings of the last ${formatCount(windowDays)} days`}
          failureNoun="your recent meetings"
          columns={columns}
          rows={request.data?.items ?? []}
          rowKey={(row) => row.id}
          status={status}
          sort={{ defaultSort: ANALYSIS_DEFAULT_SORT as SortState<AnalysisColumnKey>, namespace: 'analysis' }}
          clientSort
          empty={{
            title: `No meetings in the last ${formatCount(windowDays)} days`,
            description: 'Meetings imported from Fellow in that time appear here with what their summaries and Slack checks found.',
            action: <LinkButton to={PATHS.meetings} variant="secondary" size="sm">Open the meeting list</LinkButton>,
          }}
        />
      </CollapsibleSection>
    </div>
  );
}
